/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { SelectFieldOption } from '@squiz/sds';
import {
  EVENT_RULE_TYPE_CUSTOM,
  EVENT_RULE_TYPE_LOGIN,
  EVENT_RULE_TYPE_REQUEST,
  EVENT_RULE_TYPE_VISIT,
} from '../../../../Services/segmentation';

/* eslint-disable sort-keys-fix/sort-keys-fix */
/* eslint-disable @typescript-eslint/no-unused-vars */

export const EVENT_OPERATOR_EQUALS = `===`;
export const EVENT_OPERATOR_NOT_EQUALS = `!==`;
export const EVENT_OPERATOR_LESS_THAN = `<`;
export const EVENT_OPERATOR_GREATER_THAN = `>`;
export const EVENT_OPERATOR_LESS_THAN_EQUALS = `<=`;
export const EVENT_OPERATOR_GREATER_THAN_EQUALS = `>=`;
export const EVENT_OPERATOR_EXISTS = `exists`;
export const EVENT_OPERATOR_NOT_EXISTS = `not-exists`;
export const EVENT_OPERATOR_BEGINS_WITH = `begins-with`;
export const EVENT_OPERATOR_ENDS_WITH = `ends-with`;
export const EVENT_OPERATOR_CONTAINS = `contains`;

const EVENT_ATTRIBUTE_NAME_URL_DOMAIN = `url.domain`;
const EVENT_ATTRIBUTE_NAME_URL_PATH = `url.path`;
const EVENT_ATTRIBUTE_NAME_URL_QUERY = `url.query`;
const EVENT_ATTRIBUTE_NAME_REFERRER_DOMAIN = `referrer.domain`;
const EVENT_ATTRIBUTE_NAME_REFERRER_PATH = `referrer.path`;
const EVENT_ATTRIBUTE_NAME_REFERRER_QUERY = `referrer.query`;
const EVENT_ATTRIBUTE_NAME_LOCATION_REGION = `location.region`;
const EVENT_ATTRIBUTE_NAME_LOCATION_COUNTRY = `location.country`;
const EVENT_ATTRIBUTE_NAME_LOCATION_CONTINENT = `location.continent`;
const EVENT_ATTRIBUTE_NAME_LOCATION_CITY = `location.city`;
const EVENT_ATTRIBUTE_NAME_LOCATION_REGION_CODE = `location.regionCode`;
const EVENT_ATTRIBUTE_NAME_LOCATION_LATITUDE = `location.latitude`;
const EVENT_ATTRIBUTE_NAME_LOCATION_LONGITUDE = `location.longitude`;
const EVENT_ATTRIBUTE_NAME_LOCATION_POST_CODE = `location.postalCode`;
const EVENT_ATTRIBUTE_NAME_LOCATION_METRO_CODE = `location.metroCode`;
const EVENT_ATTRIBUTE_NAME_INTERESTS = `interests`;
const EVENT_ATTRIBUTE_NAME_AUTHORIZED = `authorized`;
const EVENT_ATTRIBUTE_NAME_IDP_ATTRIBUTES = `idp`;

/**
 * Any possible event attribute name.
 */
export type AnyEventAttributeNames = typeof EVENT_ATTRIBUTE_NAME_URL_DOMAIN &
  typeof EVENT_ATTRIBUTE_NAME_URL_PATH &
  typeof EVENT_ATTRIBUTE_NAME_URL_QUERY &
  typeof EVENT_ATTRIBUTE_NAME_REFERRER_DOMAIN &
  typeof EVENT_ATTRIBUTE_NAME_REFERRER_PATH &
  typeof EVENT_ATTRIBUTE_NAME_REFERRER_QUERY &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_REGION &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_COUNTRY &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_CONTINENT &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_CITY &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_REGION_CODE &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_LATITUDE &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_LONGITUDE &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_POST_CODE &
  typeof EVENT_ATTRIBUTE_NAME_LOCATION_METRO_CODE &
  typeof EVENT_ATTRIBUTE_NAME_INTERESTS &
  typeof EVENT_ATTRIBUTE_NAME_AUTHORIZED;

/**
 * Any of the available event types.
 */
export type AnyEventType = typeof EVENT_RULE_TYPE_REQUEST &
  typeof EVENT_RULE_TYPE_LOGIN &
  typeof EVENT_RULE_TYPE_VISIT &
  typeof EVENT_RULE_TYPE_CUSTOM;

export const EVENT_ATTRIBUTE_SELECT_OPTION_EQUALS = {
  key: `equals`,
  label: `Equals`,
  value: EVENT_OPERATOR_EQUALS,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_NOT_EQUALS = {
  key: `not-equals`,
  label: `Does not equal`,
  value: EVENT_OPERATOR_NOT_EQUALS,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_LESS_THAN = {
  key: `less-than`,
  label: `Less than`,
  value: EVENT_OPERATOR_LESS_THAN,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_GREATER_THAN = {
  key: `greater-than`,
  label: `Greater than or equals`,
  value: EVENT_OPERATOR_GREATER_THAN,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_LESS_THAN_EQUALS = {
  key: `less-than-equals`,
  label: `Less than or equals`,
  value: EVENT_OPERATOR_LESS_THAN_EQUALS,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_GREATER_THAN_EQUALS = {
  key: `greater-than-equals`,
  label: `Greater than or equals`,
  value: EVENT_OPERATOR_GREATER_THAN_EQUALS,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_EXISTS = {
  key: EVENT_OPERATOR_EXISTS,
  label: `Exists`,
  value: EVENT_OPERATOR_EXISTS,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_NOT_EXISTS = {
  key: EVENT_OPERATOR_NOT_EXISTS,
  label: `Does not exist`,
  value: EVENT_OPERATOR_NOT_EXISTS,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_BEGINS_WITH = {
  key: EVENT_OPERATOR_BEGINS_WITH,
  label: `Begins with`,
  value: EVENT_OPERATOR_BEGINS_WITH,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_ENDS_WITH = {
  key: EVENT_OPERATOR_ENDS_WITH,
  label: `Ends with`,
  value: EVENT_OPERATOR_ENDS_WITH,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_HAS_TAG = {
  key: EVENT_OPERATOR_EXISTS,
  label: `Has tag`,
  value: EVENT_OPERATOR_EXISTS,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_NOT_HAS_TAG = {
  key: `not-has-tag`,
  label: `Does not have tag`,
  value: EVENT_OPERATOR_NOT_EXISTS,
};
export const EVENT_ATTRIBUTE_SELECT_OPTION_CONTAINS = {
  key: `contains`,
  label: `Contains`,
  value: EVENT_OPERATOR_CONTAINS,
};

export const ALL_OPTIONS = [
  EVENT_ATTRIBUTE_SELECT_OPTION_EQUALS,
  EVENT_ATTRIBUTE_SELECT_OPTION_NOT_EQUALS,
  // Note: These options are supported by event bus but not enabled yet.
  // See: https://squizgroup.atlassian.net/wiki/spaces/PRODUCT/pages/2892333069/Segmentation+At+The+Edge+S+E
  // EVENT_ATTRIBUTE_SELECT_OPTION_LESS_THAN,
  // EVENT_ATTRIBUTE_SELECT_OPTION_GREATER_THAN,
  // EVENT_ATTRIBUTE_SELECT_OPTION_LESS_THAN_EQUALS,
  // EVENT_ATTRIBUTE_SELECT_OPTION_GREATER_THAN_EQUALS,
  // EVENT_ATTRIBUTE_SELECT_OPTION_EXISTS,
  // EVENT_ATTRIBUTE_SELECT_OPTION_NOT_EXISTS,
  // EVENT_ATTRIBUTE_SELECT_OPTION_BEGINS_WITH,
  // EVENT_ATTRIBUTE_SELECT_OPTION_ENDS_WITH,
];

export const comparisonOperatorSelectMap: Record<AnyEventAttributeNames, Array<SelectFieldOption>> = {
  [EVENT_ATTRIBUTE_NAME_URL_DOMAIN]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_URL_PATH]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_URL_QUERY]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_REFERRER_DOMAIN]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_REFERRER_PATH]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_REFERRER_QUERY]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_REGION]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_COUNTRY]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_CONTINENT]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_CITY]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_REGION_CODE]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_LATITUDE]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_LONGITUDE]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_POST_CODE]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_LOCATION_METRO_CODE]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_INTERESTS]: [EVENT_ATTRIBUTE_SELECT_OPTION_HAS_TAG, EVENT_ATTRIBUTE_SELECT_OPTION_NOT_HAS_TAG],
  [EVENT_ATTRIBUTE_NAME_AUTHORIZED]: ALL_OPTIONS,
  [EVENT_ATTRIBUTE_NAME_IDP_ATTRIBUTES]: [
    EVENT_ATTRIBUTE_SELECT_OPTION_EQUALS,
    EVENT_ATTRIBUTE_SELECT_OPTION_NOT_EQUALS,
    EVENT_ATTRIBUTE_SELECT_OPTION_CONTAINS,
  ],
};

export const attributeNameSelectOptionMap: Record<AnyEventType, Array<SelectFieldOption>> = {
  [EVENT_RULE_TYPE_REQUEST]: [
    {
      key: EVENT_ATTRIBUTE_NAME_URL_DOMAIN,
      label: `URL - Domain`,
      value: EVENT_ATTRIBUTE_NAME_URL_DOMAIN,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_URL_PATH,
      label: `URL - Path`,
      value: EVENT_ATTRIBUTE_NAME_URL_PATH,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_URL_QUERY,
      label: `URL - Query string`,
      value: EVENT_ATTRIBUTE_NAME_URL_QUERY,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_REFERRER_DOMAIN,
      label: `Referrer - Domain`,
      value: EVENT_ATTRIBUTE_NAME_REFERRER_DOMAIN,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_REFERRER_PATH,
      label: `Referrer - Path`,
      value: EVENT_ATTRIBUTE_NAME_REFERRER_PATH,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_REFERRER_QUERY,
      label: `Referrer - Query string`,
      value: EVENT_ATTRIBUTE_NAME_REFERRER_QUERY,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_LOCATION_CITY,
      label: `Location - City`,
      value: EVENT_ATTRIBUTE_NAME_LOCATION_CITY,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_LOCATION_CONTINENT,
      label: `Location - Continent`,
      value: EVENT_ATTRIBUTE_NAME_LOCATION_CONTINENT,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_LOCATION_COUNTRY,
      label: `Location - Country`,
      value: EVENT_ATTRIBUTE_NAME_LOCATION_COUNTRY,
    },
    // {
    //   key: EVENT_ATTRIBUTE_NAME_LOCATION_LATITUDE,
    //   label: `Location - Latitude`,
    //   value: EVENT_ATTRIBUTE_NAME_LOCATION_LATITUDE,
    // },
    // {
    //   key: EVENT_ATTRIBUTE_NAME_LOCATION_LONGITUDE,
    //   label: `Location - Longitude`,
    //   value: EVENT_ATTRIBUTE_NAME_LOCATION_LONGITUDE,
    // },
    {
      key: EVENT_ATTRIBUTE_NAME_LOCATION_REGION,
      label: `Location - Region`,
      value: EVENT_ATTRIBUTE_NAME_LOCATION_REGION,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_LOCATION_REGION_CODE,
      label: `Location - Region code`,
      value: EVENT_ATTRIBUTE_NAME_LOCATION_REGION_CODE,
    },
    // {
    //   key: EVENT_ATTRIBUTE_NAME_LOCATION_METRO_CODE,
    //   label: `Location - Metro code`,
    //   value: EVENT_ATTRIBUTE_NAME_LOCATION_METRO_CODE,
    // },
    // {
    //   key: EVENT_ATTRIBUTE_NAME_LOCATION_POST_CODE,
    //   label: `Location - Postal code`,
    //   value: EVENT_ATTRIBUTE_NAME_LOCATION_POST_CODE,
    // },
    {
      key: EVENT_ATTRIBUTE_NAME_INTERESTS,
      label: `Interest - Tags`,
      value: EVENT_ATTRIBUTE_NAME_INTERESTS,
    },
  ],
  [EVENT_RULE_TYPE_LOGIN]: [
    {
      key: EVENT_ATTRIBUTE_NAME_AUTHORIZED,
      label: `Authorized`,
      value: EVENT_ATTRIBUTE_NAME_AUTHORIZED,
    },
    {
      key: EVENT_ATTRIBUTE_NAME_IDP_ATTRIBUTES,
      label: `IDP attribute`,
      value: EVENT_ATTRIBUTE_NAME_IDP_ATTRIBUTES,
    },
  ],
  [EVENT_RULE_TYPE_VISIT]: [],
};

export const webBehaviorSelectOptions: Array<SelectFieldOption> = [];

export const eventOptions: Array<SelectFieldOption> = [
  {
    key: `event-type-page-view`,
    label: `Page View`,
    value: EVENT_RULE_TYPE_REQUEST,
  },
  {
    key: `event-type-login`,
    label: `Login`,
    value: EVENT_RULE_TYPE_LOGIN,
  },
  {
    key: `event-type-visit`,
    label: `Visit`,
    value: EVENT_RULE_TYPE_VISIT,
  },
  {
    key: `event-type-custom`,
    label: `Custom`,
    value: EVENT_RULE_TYPE_CUSTOM,
  },
];
